// IOHB-1886: the labels shown with the fields a signer fills in on the signing page. A label says what to do with the
// field and whether it's required; the form's own printed caption names the field. Forms can be crowded, so a label
// only shows where it covers nothing else on the page, except the one for the field the signer is on, which always shows.

/** What a field the signer fills in is: a text box, a list to choose from, a single checkbox, or a set of answers of
 *  which only one can be picked (radio buttons, or checkboxes that share a name). */
export type FillFieldKind = 'text' | 'list' | 'checkbox' | 'pickOne';

/** A rectangle on a drawn page, in pixels from the page's top-left corner. */
export interface PageBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Something a label must not cover: printed text, a signature box, or a field (named, so a field's own boxes don't
 *  count against its label). */
export interface PageObstacle {
  page: number;
  name?: string;
  box: PageBox;
}

/** A label for a field the signer fills in, where the field first appears on a page: the room it takes above the field
 *  while the signer is elsewhere; the spots it may take while the signer is on the field, and the one it takes when none
 *  of those has room; and where it's drawn (labelTop is null while it isn't shown). */
export interface FillFieldTag {
  name: string;
  kind: FillFieldKind;
  page: number;
  top: number;
  left: number;
  text: string;
  above: PageBox;
  currentSpots: PageBox[];
  forced: PageBox;
  pageHeight: number;
  labelTop: number | null;
  labelLeft: number;
  shown: boolean;
}

/** The gap between a label and its field, and the room a label takes: its own height and that gap. */
export const LABEL_GAP = 6;
export const LABEL_ROOM = 18;

const MAX_LABEL_LENGTH = 40;

/** Names form tools give fields by default, once their numbers are taken off ("Text12" is "Text", "Check Box3" is
 *  "Check Box"), in lower case, spelt with and without the space. */
const MADE_UP_NAMES = new Set([
  'text',
  'text field',
  'textfield',
  'text box',
  'textbox',
  'check box',
  'checkbox',
  'radio',
  'radio button',
  'radiobutton',
  'group',
  'choice',
  'combo box',
  'combobox',
  'list box',
  'listbox',
  'drop down',
  'dropdown',
  'button',
  'field',
  'undefined',
  'untitled',
  'fill in',
  'fillin',
  'form',
]);

/** Takes the numbers (and spaces) off the end of a name, one character at a time, so it takes as long as the name. */
const withoutTrailingNumbers = (text: string): string => {
  let end = text.length;
  while (end > 0 && /[\s\d]/.test(text.charAt(end - 1))) end--;
  return text.slice(0, end);
};

const shorten = (text: string): string =>
  text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH - 1).trimEnd()}…` : text;

/** A field name as a person would write it ("name_of_agent" is "Name of agent", "Address_3" is "Address",
 *  "io_primaryPhysician" is "Primary Physician"), or null for a name a form tool made up ("1_3", "Text12"). */
export function readableFieldName(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const words = withoutTrailingNumbers(
    name
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_\-.]+/g, ' ')
      .replace(/^\s*io\s+/i, ''),
  )
    .replace(/\s+/g, ' ')
    .trim();
  // A single letter is a name a form tool took from the words beside the field ("I, ____"): it tells a signer nothing.
  if (words.length < 2 || !/[a-z]/i.test(words) || MADE_UP_NAMES.has(words.toLowerCase())) return null;
  return shorten(words.charAt(0).toUpperCase() + words.slice(1));
}

/** The tooltip text a PDF form gives a field, when it's something a person can read rather than a made-up name. */
export function readableTooltip(text: unknown): string | null {
  if (typeof text !== 'string') return null;
  const tooltip = text.replace(/\s+/g, ' ').trim();
  if (!tooltip || readableFieldName(tooltip) === null) return null;
  return shorten(tooltip);
}

/** The words that name a field for a screen reader: its tooltip text, else its readable name, else null. */
export function fillFieldLabel(name: unknown, tooltip: unknown): string | null {
  return readableTooltip(tooltip) ?? readableFieldName(name);
}

/** A field's label: what to do with it and whether it's required. It doesn't repeat the field's name: the printed
 *  caption beside the field already gives it, and the names forms carry are often made up or mistyped by the tool
 *  that added the fields. A text box's own guide text says "Type here", so its label just says Required or Optional. */
export function fillFieldTagText(kind: FillFieldKind, required: boolean): string {
  const need = required ? 'Required' : 'Optional';
  switch (kind) {
    case 'pickOne':
      return `Select one · ${need}`;
    case 'checkbox':
      return required ? 'Check the box · Required' : 'Check if it applies · Optional';
    case 'list':
      return `Select from the list · ${need}`;
    default:
      return need;
  }
}

/** Where a label goes above or below its field: lined up with the field's left edge (or its right edge), moved left as far
 *  as the page's right edge needs, and never wider than the page. */
export function labelBox(
  field: { top: number; left: number; width: number; height: number },
  labelWidth: number,
  pageWidth: number,
  side: 'above' | 'below',
  align: 'left' | 'right' = 'left',
): PageBox {
  const width = Math.min(labelWidth, pageWidth);
  const start = align === 'left' ? field.left : field.left + field.width - width;
  return {
    left: Math.max(0, Math.min(start, pageWidth - width)),
    top: side === 'above' ? field.top - LABEL_ROOM : field.top + field.height,
    width,
    height: LABEL_ROOM,
  };
}

/** Where the label of the field the signer is on may go, in order: above the field, else below it, lined up with its left
 *  edge; for a text box or list at least as wide as the label, then with its right edge too. When none of those has room
 *  it goes below the field anyway (above it at the foot of the page), at the right end of a wide field, where forms
 *  leave the field empty and put their captions on the left, so it covers as little as it can. */
export function currentFieldSpots(
  field: { top: number; left: number; width: number; height: number },
  kind: FillFieldKind,
  labelWidth: number,
  pageWidth: number,
  pageHeight: number,
): { spots: PageBox[]; forced: PageBox } {
  const wide = (kind === 'text' || kind === 'list') && field.width >= labelWidth;
  const aboveLeft = labelBox(field, labelWidth, pageWidth, 'above');
  const belowLeft = labelBox(field, labelWidth, pageWidth, 'below');
  if (!wide) {
    return { spots: [aboveLeft, belowLeft], forced: belowLeft.top + belowLeft.height <= pageHeight ? belowLeft : aboveLeft };
  }
  const aboveRight = labelBox(field, labelWidth, pageWidth, 'above', 'right');
  const belowRight = labelBox(field, labelWidth, pageWidth, 'below', 'right');
  return {
    spots: [aboveLeft, belowLeft, aboveRight, belowRight],
    forced: belowRight.top + belowRight.height <= pageHeight ? belowRight : aboveRight,
  };
}

/** True when two boxes cover more than a pixel of each other. */
export function boxesOverlap(a: PageBox, b: PageBox): boolean {
  const across = Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left);
  const down = Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top);
  return across > 1 && down > 1;
}

/** Where each label goes, taking the labels in the order given: the first of its spots that stays on its page and
 *  covers nothing (printed text, another field, a signature box or a label already placed), else its forced spot if
 *  it has one, else nowhere (null). */
export function placeLabels(
  labels: { page: number; name: string; spots: PageBox[]; forced?: PageBox; pageHeight: number }[],
  obstacles: PageObstacle[],
): (PageBox | null)[] {
  const placed: { page: number; box: PageBox }[] = [];
  return labels.map(label => {
    const hasRoom = (box: PageBox) =>
      box.top >= 0 &&
      box.top + box.height <= label.pageHeight &&
      !obstacles.some(o => o.page === label.page && o.name !== label.name && boxesOverlap(o.box, box)) &&
      !placed.some(p => p.page === label.page && boxesOverlap(p.box, box));
    const box = label.spots.find(hasRoom) ?? label.forced ?? null;
    if (box) placed.push({ page: label.page, box });
    return box;
  });
}
